import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
} from 'antd';
import { space, useNarrowViewport } from '@viq-admin/ui';
import {
  analyzeSheet,
  ingestSheet,
  type AnalyzeSheetResponse,
  type CanonicalField,
  type IngestSheetResponse,
  type SheetMapping,
} from '../../api/attendeeSheet';
import { Can } from '../../ability/AbilityContext';

/**
 * Per-conference Attendee sheet tab (super-admin only): upload an .xlsx,
 * review the proposed column mapping, confirm, see the ingest result.
 *
 * Four steps — choose file -> review mapping -> confirm -> result. The file is
 * read in the browser (FileReader -> base64) and sent as JSON; `analyzeSheet`
 * writes nothing, `ingestSheet` REPLACES the conference's active sheet and
 * resets lead match history (hence the confirm-step warning, shown whether or
 * not a sheet already exists — the client can't tell).
 *
 * The selected file's base64 survives errors and a 409 (file differs from the
 * analyzed one), so the user re-runs analyze without re-picking the file.
 * Writes are gated on `update Conference` (UX-only; the backend enforces it).
 */

const MAX_BYTES = 20 * 1024 * 1024;

const FIELD_OPTIONS: { value: CanonicalField; label: string }[] = [
  { value: 'first_name', label: 'First name' },
  { value: 'last_name', label: 'Last name' },
  { value: 'company', label: 'Company' },
  { value: 'title', label: 'Title' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'address', label: 'Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'postal_code', label: 'Postal code' },
  { value: 'country', label: 'Country' },
];

/** Select sentinel for "Not mapped" (rc-select rejects a null option value). */
const NONE = '__none__';

const STEP = { file: 0, review: 1, confirm: 2, result: 3 } as const;

/** ArrayBuffer -> base64, chunked so large files don't blow the call stack. */
function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(toBase64(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsArrayBuffer(file);
  });
}

interface PickedFile {
  name: string;
  contentBase64: string;
}

export function ConferenceAttendeeSheet({
  conferenceId,
}: {
  conferenceId: string;
}) {
  return (
    <Can I="update" a="Conference" passThrough>
      {(allowed: boolean) =>
        allowed ? (
          <AttendeeSheetFlow conferenceId={conferenceId} />
        ) : (
          <Alert
            type="info"
            showIcon
            message="Read-only: your role cannot upload an attendee sheet."
          />
        )
      }
    </Can>
  );
}

function AttendeeSheetFlow({ conferenceId }: { conferenceId: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const narrow = useNarrowViewport();
  const [step, setStep] = useState<number>(STEP.file);
  const [picked, setPicked] = useState<PickedFile | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeSheetResponse | null>(null);
  const [mapping, setMapping] = useState<SheetMapping>({});
  const [result, setResult] = useState<IngestSheetResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setStep(STEP.file);
    setPicked(null);
    setAnalysis(null);
    setMapping({});
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setPicked(null);
      setError('Only .xlsx files are supported.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setPicked(null);
      setError('That file is larger than the 20MB limit. Please upload a smaller file.');
      return;
    }
    try {
      setPicked({ name: file.name, contentBase64: await readAsBase64(file) });
    } catch (err) {
      setPicked(null);
      setError((err as { message?: string }).message ?? 'Could not read the file.');
    }
  }

  async function onAnalyze() {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const res = await analyzeSheet(conferenceId, {
        fileName: picked.name,
        contentBase64: picked.contentBase64,
      });
      setAnalysis(res);
      // Every header gets an explicit entry so the Selects are controlled.
      setMapping(
        Object.fromEntries(res.header.map((h) => [h, res.mapping[h] ?? null])),
      );
      setStep(STEP.review);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Analysis failed.');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit() {
    if (!picked || !analysis) return;
    setBusy(true);
    setError(null);
    try {
      const res = await ingestSheet(conferenceId, {
        fileName: picked.name,
        contentBase64: picked.contentBase64,
        mapping,
        expectedContentHash: analysis.contentHash,
      });
      setResult(res);
      setStep(STEP.result);
    } catch (err) {
      const e = err as { statusCode?: number; message?: string };
      if (e.statusCode === 409) {
        // File differs from the analyzed one: back to step one with the file
        // still selected, so the user just re-runs analyze.
        setAnalysis(null);
        setStep(STEP.file);
        setError(
          'The file no longer matches the one that was analyzed. Re-run the analysis to continue.',
        );
      } else {
        setError(e.message ?? 'Upload failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  const mappedFields = Object.values(mapping).filter(
    (v): v is CanonicalField => v != null,
  );
  const hasNames =
    mappedFields.includes('first_name') && mappedFields.includes('last_name');

  return (
    <Card title="Attendee sheet">
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: space.lg }}
        items={[
          { title: 'Choose file' },
          { title: 'Review mapping' },
          { title: 'Confirm' },
          { title: 'Result' },
        ]}
      />

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: space.lg }}
        />
      )}

      {step === STEP.file && (
        <Space direction="vertical" size="middle">
          <Typography.Text type="secondary">
            Upload the conference's attendee list (.xlsx, up to 20MB).
          </Typography.Text>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            aria-label="Attendee sheet file"
            onChange={onPick}
          />
          {picked && <Typography.Text>Selected: {picked.name}</Typography.Text>}
          <Button
            type="primary"
            disabled={!picked}
            loading={busy}
            onClick={onAnalyze}
          >
            Analyze
          </Button>
        </Space>
      )}

      {step === STEP.review && analysis && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions size="small" column={narrow ? 1 : 3}>
            <Descriptions.Item label="Worksheet">
              {analysis.worksheetName}
            </Descriptions.Item>
            <Descriptions.Item label="Rows">{analysis.rowCount}</Descriptions.Item>
            <Descriptions.Item label="Mapping">
              {analysis.mappingSource === 'model' ? (
                <Tag color="purple">Suggested by AI</Tag>
              ) : (
                <Tag>Default aliases</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Table
            size="small"
            pagination={false}
            rowKey="header"
            dataSource={analysis.header.map((header) => ({ header }))}
            columns={[
              { title: 'Column', dataIndex: 'header' },
              {
                title: 'Maps to',
                render: (_: unknown, { header }: { header: string }) => (
                  <Select
                    style={{ minWidth: 200 }}
                    aria-label={`Map column ${header}`}
                    value={mapping[header] ?? NONE}
                    onChange={(v: string) =>
                      setMapping((m) => ({
                        ...m,
                        [header]: v === NONE ? null : (v as CanonicalField),
                      }))
                    }
                    options={[
                      { value: NONE, label: 'Not mapped' },
                      ...FIELD_OPTIONS.map((o) => ({
                        ...o,
                        // A field may map to only one column.
                        disabled: Object.entries(mapping).some(
                          ([h, f]) => h !== header && f === o.value,
                        ),
                      })),
                    ]}
                  />
                ),
              },
              {
                title: '',
                render: (_: unknown, { header }: { header: string }) => {
                  if (mapping[header] != null) return null;
                  return analysis.conflicted.includes(header) ? (
                    <Tag color="orange">
                      Conflict: another column already claimed this field
                    </Tag>
                  ) : (
                    <Tag color="warning">Unmapped</Tag>
                  );
                },
              },
            ]}
          />

          <Typography.Text strong>Preview (first rows)</Typography.Text>
          <Table
            size="small"
            pagination={false}
            scroll={{ x: true }}
            rowKey="idx"
            dataSource={analysis.preview.map((row, idx) => ({ idx, row }))}
            columns={analysis.header.map((h, i) => ({
              title: h,
              key: `${i}`,
              render: (_: unknown, rec: { row: string[] }) => rec.row[i],
            }))}
          />

          {!hasNames && (
            <Alert
              type="warning"
              showIcon
              message="Map both First name and Last name to continue."
            />
          )}
          <Space>
            <Button onClick={reset}>Start over</Button>
            <Button
              type="primary"
              disabled={!hasNames}
              onClick={() => setStep(STEP.confirm)}
            >
              Confirm mapping
            </Button>
          </Space>
        </Space>
      )}

      {step === STEP.confirm && analysis && picked && (
        <Space direction="vertical" size="middle">
          <Alert
            type="warning"
            showIcon
            message="This replaces the conference's current attendee sheet, if there is one, and resets lead match history."
          />
          <Typography.Text>
            {picked.name}: {analysis.rowCount} rows from worksheet "
            {analysis.worksheetName}", {mappedFields.length} columns mapped.
          </Typography.Text>
          <Space>
            <Button onClick={() => setStep(STEP.review)} disabled={busy}>
              Back
            </Button>
            <Button type="primary" danger loading={busy} onClick={onSubmit}>
              Upload and replace
            </Button>
          </Space>
        </Space>
      )}

      {step === STEP.result && result && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="success"
            showIcon
            message={`${result.rowCount} rows accepted from "${result.worksheetName}".`}
          />
          {result.rejected.length > 0 && (
            <Collapse
              defaultActiveKey={result.rejected.length <= 5 ? ['rejected'] : []}
              items={[
                {
                  key: 'rejected',
                  label: `${result.rejected.length} rows rejected`,
                  children: (
                    <Table
                      size="small"
                      rowKey="line"
                      pagination={
                        result.rejected.length > 10 ? { pageSize: 10 } : false
                      }
                      dataSource={result.rejected}
                      columns={[
                        { title: 'Line', dataIndex: 'line', width: 100 },
                        { title: 'Reason', dataIndex: 'reason' },
                      ]}
                    />
                  ),
                },
              ]}
            />
          )}
          <Button onClick={reset}>Upload another</Button>
        </Space>
      )}
    </Card>
  );
}
